"use client";

import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router";
import { ShoppingCart, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuthStore } from "@/store/AuthStore";
import { useGetCartItems, useGetGuestCart } from "@/apis/cart/Queries";
import { useCartStore, type ICartItem } from "@/store/CartStore";
import { useRemoveFromCart } from "@/apis/cart/Mutations";

const CartPopover = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const user = useAuthStore((state) => state.user);
  const { data: CartItemsQuery } = useGetCartItems();

  const cartStoreItems = useCartStore((state) => state.items);
  const removeFromCart = useCartStore((state) => state.removeFromCart);
  const RemoveFromCart = useRemoveFromCart();

  const { data: guestCartData } = useGetGuestCart(!user && isOpen ? cartStoreItems : []);

  React.useEffect(() => {
    if (!user && isOpen && guestCartData?.items && cartStoreItems.length > 0) {
      if (guestCartData.items.length !== cartStoreItems.length) {
        useCartStore.getState().setCart(guestCartData.items);
      }
    }
  }, [user, isOpen, guestCartData, cartStoreItems.length]);

  const cartItems: ICartItem[] = user
    ? CartItemsQuery?.items ?? []
    : cartStoreItems;

  const cartCount = useMemo(
    () =>
      cartItems.reduce(
        (total: number, item: ICartItem) => total + item.quantity,
        0
      ),
    [cartItems]
  );

  const cartTotal = useMemo(
    () =>
      cartItems.reduce(
        (total: number, item: ICartItem) =>
          total + item.priceAtCart * item.quantity,
        0
      ),
    [cartItems]
  );

  const handleRemoveCartItem = useCallback(
    (productId: string, variant: ICartItem["variant"]) => {
      if (user) {
        const data = {
          productId: productId,
          variantId: variant?._id || null,
        };
        RemoveFromCart.mutate(data);
      } else {
        removeFromCart(productId, variant?._id);
      }
    },
    [user, RemoveFromCart, removeFromCart]
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 sm:p-3 hover:bg-gray-50 transition-colors duration-200 rounded-md outline-none">
          <ShoppingCart
            size={18}
            className="text-white font-medium md:text-gray-700"
          />
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-white md:bg-red-500 text-red-500 md:text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] h-[18px] flex items-center justify-center">
              {cartCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4 bg-white border border-gray-200 rounded-lg shadow-xl z-50">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-800">
            {t("yourCart")} ({cartCount} {t("items")})
          </h3>
        </div>

        {cartCount > 0 ? (
          <div className="space-y-3">
            <div className="max-h-60 overflow-y-auto scrollbar-hide space-y-2">
              {cartItems.map((item: ICartItem, index: number) => (
                <div
                  key={`${item.product?._id}-${
                    item?.variant?._id || "no-variant"
                  }-${index}`}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-150"
                >
                  <Link
                    to={`/product/${item.product?._id}`}
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                  >
                    <img
                      src={
                        item.variant?.images?.[0] ||
                        item.product?.productImages?.[0] ||
                        "/placeholder.svg"
                      }
                      alt={item.product?.productName || "Product"}
                      width={50}
                      height={50}
                      className="rounded-md object-cover flex-shrink-0"
                      loading="lazy"
                    />
                    <div className="flex-1 min-w-0">
                      <div>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {item.product?.productName}
                        </p>
                        <p className="text-sm font-medium text-gray-600 truncate">
                          Qty: {item.quantity}
                        </p>
                      </div>
                      <p className="text-sm font-medium text-gray-900">
                        ₹{(item.priceAtCart * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </Link>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200"
                    onClick={() =>
                      handleRemoveCartItem(item.product._id, item.variant)
                    }
                  >
                    <X size={16} />
                  </Button>
                </div>
              ))}
            </div>

            <div className="border-t pt-3">
              <div className="flex justify-between items-center font-semibold text-gray-900 mb-3">
                <span>Total Amount</span>
                <span className="text-lg">₹{cartTotal.toFixed(2)}</span>
              </div>

              <Button
                onClick={() => {
                  setIsOpen(false);
                  navigate("/cart");
                }}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2.5 rounded-lg transition-colors duration-200"
              >
                {t("proceedToCheckout")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <ShoppingCart className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">{t("cartEmpty")}</p>
            <p className="text-sm text-gray-400 mt-1">Add items to your cart</p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default CartPopover;
